import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { fastestPollInterval, getEmailDeliveryPollInterval } from '@/lib/email-delivery';
import { receiptKeys } from '@/lib/queryKeys';
import type { ReceiptListResponse, ReceiptKind, ReceiptDeliveryStatusFilter } from './types';

/**
 * Resend already invalidates the list on completion, but the send itself
 * finishes asynchronously afterward — without this, a queued row never
 * settles to Sent or Failed without a reload. Poll while ANY visible row is
 * still queued (its receipt email or its bank-authorization copy), at the
 * fastest interval any of them needs; the per-row interval comes from
 * `getEmailDeliveryPollInterval` rather than being re-derived here.
 */
function pollWhileAnyEmailQueued(query: { state: { data?: ReceiptListResponse } }): number | false {
  const rows = query.state.data?.data ?? [];
  let fastest: number | false = false;
  for (const row of rows) {
    fastest = fastestPollInterval(
      fastest,
      getEmailDeliveryPollInterval(row.emailDelivery),
      getEmailDeliveryPollInterval(row.achAuthorizationCopy),
    );
  }
  return fastest;
}

export interface UseReceiptsParams {
  search?: string;
  contactId?: string;
  kind?: ReceiptKind;
  from?: string;
  to?: string;
  receiptNumber?: string;
  invoiceNumber?: string;
  method?: string;
  totalEq?: number;
  totalMin?: number;
  totalMax?: number;
  deliveryStatus?: ReceiptDeliveryStatusFilter;
  sortBy?: 'generatedAt' | 'totalAmount' | 'number';
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export function useReceipts(params: UseReceiptsParams = {}, options: { enabled?: boolean } = {}) {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.contactId) search.set('contactId', params.contactId);
  if (params.kind) search.set('kind', params.kind);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.receiptNumber) search.set('receiptNumber', params.receiptNumber);
  if (params.invoiceNumber) search.set('invoiceNumber', params.invoiceNumber);
  if (params.method) search.set('method', params.method);
  if (params.totalEq !== undefined) search.set('totalEq', String(params.totalEq));
  if (params.totalMin !== undefined) search.set('totalMin', String(params.totalMin));
  if (params.totalMax !== undefined) search.set('totalMax', String(params.totalMax));
  if (params.deliveryStatus) search.set('deliveryStatus', params.deliveryStatus);
  if (params.sortBy) search.set('sortBy', params.sortBy);
  if (params.sortDir) search.set('sortDir', params.sortDir);
  if (params.page) search.set('page', String(params.page));
  if (params.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const url = qs ? `${endpoints.receipts.list()}?${qs}` : endpoints.receipts.list();

  return useApiQuery<ReceiptListResponse>({
    queryKey: receiptKeys.list(params),
    queryFn: () => apiGet<ReceiptListResponse>(operationsClient, url),
    enabled: options.enabled,
    refetchInterval: pollWhileAnyEmailQueued,
  });
}
